import React, { useContext, useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";

import { Provider } from "../../components/alert/AlertController.jsx";
import Template from "./Cart.jsx";
import { get, put } from "../../util/fetch.js";
import { Context as LoginContext } from "../../components/login/LoginController.jsx";
import { useFavourite } from "./hooks/useFavourite.jsx";
import { useIntl } from "react-intl";
import { removeDiacritics } from "../../util/script.js";

function Cart(props) {
  const [cartInit, setCartInit] = useState(true);
  const [activeSearch, setActiveSearch] = useState(false);
  const [subscription, setSubscription] = useState(props.delivery.subscription);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [fromFooter, setFromFooter] = useState(false);
  const [productType, setProductType] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [rulesProd, setRulesProd] = useState([]);
  const [hasRules, setHasRules] = useState(false);
  const [ruleCount, setRuleCount] = React.useState(0);
  const [preferencesAndRulesLoaded, setPreferencesAndRulesLoaded] =
    useState(false);
  const [isLoadingPreferencesAndRules, setIsLoadingPreferencesAndRules] =
    useState(false);

  const [tracker, setTracker] = useState(false);
  const [filteredProductType, setFilteredProductType] = useState([]);
  const [userLikedProducts, setUserLikedProducts] = useState([]);
  const [showPreferences, setShowPreferences] = useState(false);
  const [reRender, setRender] = useState(false);
  const [deliveryAlteredText, setDeliveryAlteredText] = useState("");
  const [insufficientStockAmountText, setInsufficientStockAmountText] =
    useState("");
  const [showCartUnsoldAlert, setShowCartUnsoldAlert] = useState(true);
  const [hasPayment, setHasPayment] = useState(props.hasPayment);
  const [searchText, setSearchText] = useState("");
  const [filteredCatalog, setFilteredCatalog] = useState();
  const {
    colorSecondary,
    logoSmallUrl,
    companyAddress,
    bioCertification,
    taxDetails,
    privacyPage,
  } = props;
  const cleanText = (str) => removeDiacritics(str || "").toLowerCase();

  const search = (text) => {
    setSearchText(text);
    const cleanedText = cleanText(text);

    if (cleanedText.length > 0 && catalog) {
      setActiveSearch(true);
      const tempFilteredCatelog = catalog.filter((item) => {
        const matchArticleNameAndQualities = cleanText(
          item.article_name_and_qualities,
        ).includes(cleanedText);
        const matchCategories = item.categories.some((sub_category) => {
          return cleanText(sub_category.category).includes(cleanedText) ||
                 cleanText(sub_category.subcategory).includes(cleanedText);
        });
        const matchProducer = item.article.producer
          ? cleanText(item.article.producer).includes(cleanedText)
          : false;
        return matchArticleNameAndQualities || matchCategories || matchProducer;
      });
      setFilteredCatalog(tempFilteredCatelog);
      return tempFilteredCatelog;
    } else {
      setActiveSearch(false);
      setFilteredCatalog([...catalog]);
      return [...catalog];
    }
  };

  const {
    isCatalogEmpty,
    productsByCategory,
    filteredProductsByCategory,
    onFavouriteAddRemove,
    delivery,
    setDelivery,
  } = useFavourite(
    categories,
    catalog,
    searchText,
    filteredCatalog,
    props.delivery,
    props.setDelivery,
    search,
  );
  const intl = useIntl();
  const { setIsAuthenticated } = useContext(LoginContext);
  const fetchCatalog = async () => {
    const url = `${location.origin}/api/delivery/${delivery.id}/catalog`;
    await get(url)
      .then((resp) => {
        const fullCatalog = resp.catalog.map((item) => {
          const dynamicItem = resp.dynamic_catalog.find(
            (d) => d.id === item.id,
          );
          return { ...item, ...dynamicItem };
        });
        setCategories([...resp.all_categories]);
        setCatalog([...fullCatalog]);
        setCatalogLoaded(true);
      })
      .catch((error) => {
        console.log("error: ", error);
      });
  };

  const fetchDelivery = async () => {
    const url = `${location.origin}/api/delivery/${delivery.id}`;
    await get(url)
      .then((delivery) => {
        partialUpdateCart(delivery);
        updateCart({ sold_and_edited: delivery.sold_and_edited });
        setRender(!reRender);
        setDelivery({ ...delivery });
        setSubscription(delivery.subscription);
      })
      .catch((error) => {
        console.log("error on fetch delivery: ", error);
      });
  };

  useEffect(() => {
    if (!catalogLoaded) {
      fetchCatalog().catch((error) => {
        console.log("error", error);
      });
    }

    const myDelivery = delivery;
    const newCartOrders = mapNewCartOrders(myDelivery);
    myDelivery.orders = newCartOrders;

    updateCart(myDelivery);
    if (!isLoadingPreferencesAndRules && !preferencesAndRulesLoaded) {
      fetchPreferencesAndRules(delivery.subscription.id);
    }
    if (preferencesAndRulesLoaded && !tracker) {
      initRulesProduct();
      setTracker(true);
    }
    setRender(!reRender);
  }, [tracker, catalogLoaded, preferencesAndRulesLoaded, userLikedProducts]);

  useEffect(() => {
    if (catalogLoaded) {
      const itemToBeAddedString = localStorage.getItem("toBeAddedCartItemId");
      const itemToBeAdded = JSON.parse(itemToBeAddedString);
      if (!itemToBeAddedString) return;
      const isItemAvailable = catalog.find(
        (catalogItem) => catalogItem.id === itemToBeAdded.deliver_listing.id,
      );
      if (itemToBeAdded && !isItemAvailable?.display_inactive) {
        onChangeItems(itemToBeAdded);
      }
      localStorage.clear();
    }
  }, [catalogLoaded]);

  const fetchPreferencesAndRules = React.useCallback(
    (subscriptionId, forceUpdate = false) => {
      if (isLoadingPreferencesAndRules) return;
      setIsLoadingPreferencesAndRules(true);
      const url = `${location.origin}/api/subscription-listing-rule/${subscriptionId}`;
      get(url)
        .then((resp) => {
          setProductType(resp.deliver_listings);
          setRulesProd(resp.rules_product);
          setHasRules(resp.rules_product ? true : false);

          const tempRulesCount = resp.rules_product.filter(
            (rule) => rule.is_temporary && rule.mode === "subscribe",
          ).length;

          setRuleCount(tempRulesCount);
          setPreferencesAndRulesLoaded(true);
          if (forceUpdate) {
            initRulesProduct(resp.rules_product, resp.deliver_listings, true);
          }
        })
        .catch((error) => {
          console.log("error: ", error);
        })
        .finally(() => {
          setIsLoadingPreferencesAndRules(false);
        });
    },
  );

  const initRulesProduct = React.useCallback(
    (
      rulesProduct = undefined,
      deliverListings = undefined,
      forceUpdate = false,
    ) => {
      rulesProduct = rulesProduct || rulesProd;
      deliverListings = deliverListings || productType;
      if (forceUpdate || userLikedProducts.length === 0) {
        const userLikedProducts = rulesProduct.map((rule) => {
          const matchedListing = deliverListings.find(
            (listing) => listing.id === rule.listing_id,
          );
          return {
            ...matchedListing,
            ...rule,
          };
        });
        setUserLikedProducts(userLikedProducts);
      }
    },
  );

  const mapNewCartOrders = useCallback((myDelivery) => {
    return myDelivery.orders.map((cartItem) => {
      const matchedCartItemFromCatalog = catalog
        ? catalog.filter((catalogItem) => {
            if (cartItem.deliver_listing) {
              return catalogItem.id === cartItem.deliver_listing.id;
            } else {
              return catalogItem.id === cartItem.id;
            }
          })
        : [];
      if (
        matchedCartItemFromCatalog[0] &&
        matchedCartItemFromCatalog[0].sale_price_pro_bu !==
          cartItem.sale_price_pro_bu
      ) {
        cartItem.sale_price_pro_bu =
          matchedCartItemFromCatalog[0].sale_price_pro_bu;
      }
      return cartItem;
    });
  });

  function onSettingsClose() {
    setShowSettings(false);
    setShowPreferences(false);
  }

  function onTopClick() {
    setShowSettings(true);
  }

  function showPreferencesFunc(params) {
    setShowPreferences(true);
  }

  async function onRegisterSuccess(
    customer,
    fromRegistrationAndPaypal = false,
  ) {
    const MyDelivery = delivery;
    MyDelivery.subscription.customer = customer;
    setDelivery(MyDelivery);
    setHasPayment(
      !!(
        customer.stripe_id &&
        (customer.payment_source || customer.payment_method)
      ),
    );
    setIsAuthenticated(customer.is_authenticated);

    await onSubmit(fromRegistrationAndPaypal, MyDelivery);
  }

  function onSettingsChange(subscription) {
    const MyDelivery = delivery;
    MyDelivery.subscription = subscription;
    setDelivery(MyDelivery);
  }

  function disableButton() {
    setHasBeenClicked(true);
  }

  function updateDeliveryAlteredMessage(resp) {
    setDeliveryAlteredText(resp.adapted_delivery_message || "");
  }
  function updateInsufficientStockAmountMessage(resp) {
    setInsufficientStockAmountText(resp.insufficient_stock_message || "");
  }

  function onSubmit(fromRegistrationAndPaypal = false, newDelivery) {
    const {
      shouldRegister,
      hasBeenClicked: _hasBeenClicked,
      colorSecondary,
      logoSmallUrl,
    } = props;
    const { url, sold } = delivery;
    const waiting_text = intl.formatMessage({ id: "havePatience" });
    const confirmation_alter_text = intl.formatMessage({
      id: "orderConfirmation",
    });

    const cancelled_text = intl.formatMessage({ id: "cancelConfirmation" });
    const paused_text = intl.formatMessage({ id: "pausedDelivery" });

    let confirmation_text;
    confirmation_text =
      sold && delivery.orders.length === 0
        ? cancelled_text
        : !sold && delivery.orders.length === 0
          ? paused_text
          : confirmation_alter_text;

    const showWaitingAlert = () => {
      props.alert.show({
        message: waiting_text,
        colorsecondary: colorSecondary,
        logosmallurl: logoSmallUrl,
      });
    };
    const handlePaymentFlow = () => {
      if (fromRegistrationAndPaypal) {
        showWaitingAlert();
        return;
      }
      if (shouldRegister) {
        setShowRegistrationForm(true);
        return;
      }
      if (!hasPayment) {
        setShowRegistrationForm(true);
        return -1;
      }
      showWaitingAlert();
    };
    if (sold) {
      updateCart({ sold: false });
    }

    if (!hasBeenClicked) {
      const res = handlePaymentFlow();
      if (res === -1) return;

      disableButton(hasBeenClicked, sold);
      put(url)
        .then((resp) => {
          updateCart({
            ...newDelivery,
            sold: resp.sold,
            sold_and_edited: false,
          });
          updateDeliveryAlteredMessage(resp);
          if (!shouldRegister || fromRegistrationAndPaypal) {
            props.alert.show({
              message:
                confirmation_text + (resp.adapted_delivery_message || ""),
              colorsecondary: colorSecondary,
              logosmallurl: logoSmallUrl,
            });
          }
        })
        .then(() => fetchDelivery())
        .catch();
    }
  }

  function updateCart(myDelivery) {
    setDelivery({ ...delivery, ...myDelivery });
    setHasBeenClicked(false);
  }

  function partialUpdateCart(partialDelivery) {
    delivery.orders = partialDelivery.orders;

    delivery.delivery_sale_price_in_cart_tax_customer_dependent =
      partialDelivery.delivery_sale_price_in_cart_tax_customer_dependent;
    delivery.under_minimum_sale_price =
      partialDelivery.under_minimum_sale_price;

    delivery.delivery_fee = partialDelivery.delivery_fee;
    delivery.deposit_fee = partialDelivery.deposit_fee;
    delivery.deposit_sum = partialDelivery.deposit_sum;
    delivery.remaining_to_min_sale_price =
      partialDelivery.remaining_to_min_sale_price;
    delivery.sold_and_edited = true;
    setCartInit(false);
  }

  function onChangeItems(item, syncWithRule = false) {
    if (showSettings) {
      onSettingsClose();
    }

    put(delivery.update_url, {
      body: {
        id: item.id,
        listing_id: item.deliver_listing.id,
        increment: item.action,
        sync_with_rule: syncWithRule,
      },
    })
      .then((resp) => {
        partialUpdateCart(resp.delivery);
        updateCart({ sold_and_edited: resp.delivery.sold_and_edited });
        setShowCartUnsoldAlert(false);
        updateInsufficientStockAmountMessage(resp);
        if (
          item.rule &&
          item.rule.is_temporary &&
          item.action === -1 &&
          resp.deleted_rules_count === 1
        ) {
          setRuleCount((prevCount) => prevCount - 1);
        }
        if (resp.insufficient_stock_message) {
          props.alert.show({
            message: resp.insufficient_stock_message,
            colorsecondary: colorSecondary,
            logosmallurl: logoSmallUrl,
          });
        }
        fetchCatalog();
        setRender(!reRender);

        setDelivery((prevDelivery) => ({
          ...prevDelivery,
          orders: resp.delivery.orders,
        }));
      })
      .catch();
  }

  function debouncedOnRemoveItems(item, failCallback) {
    return new Promise((resolve, reject) => {
      const debouncedFn = debounce(() => {
        return put(delivery.remove_item_url, {
          body: {
            id: item.id,
            listing_id: item.deliver_listing.id,
          },
        })
          .then((resp) => {
            partialUpdateCart(resp.delivery);
            updateCart({ sold_and_edited: resp.delivery.sold_and_edited });
            setShowCartUnsoldAlert(false);
            fetchCatalog();

            if (item.rule && item.rule.is_temporary) {
              setRuleCount((prevCount) => prevCount - 1);
            }

            setRender(!reRender);
            resolve(resp);
          })
          .catch((error) => {
            reject(error);
            failCallback();
          });
      }, 500);

      debouncedFn();
    });
  }

  function onSwitchToEarlierDateIfExists() {
    put(delivery.nearest_delivery_day_url, {})
      .then((resp) => {
        window.location.href = resp.delivery_absolute_url;
      })
      .catch((error) => {
        console.error("Failed to switch to the earlier delivery date:", error);
      });
  }

  function onPropagateOrderedAmount() {
    put(delivery.propagate_ordered_amount_url, {})
      .then((resp) => {
        partialUpdateCart(resp.delivery);
        updateCart({ sold_and_edited: resp.delivery.sold_and_edited });
        fetchCatalog();
        setRender(!reRender);
      })
      .catch();
  }

  const toggleForceEditable = async (deliveryId) => {
    try {
      const response = await fetch(
        `/api/delivery/${deliveryId}/toggle-force-editable/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Not authorized to perform this action");
        }
        throw new Error("Failed to toggle force editable");
      }

      const result = await response.json();
      await fetchDelivery();
      return result;
    } catch (error) {
      console.error("Error toggling force editable:", error);
      throw error;
    }
  };

  return (
    <Template
      activeSearch={activeSearch}
      setActiveSearch={setActiveSearch}
      delivery={delivery}
      catalog={catalog}
      catalogLoaded={catalogLoaded}
      shouldRegister={props.shouldRegister}
      hasPayment={hasPayment}
      browser_is_IE={props.browser_is_IE}
      showRegistrationForm={showRegistrationForm}
      setShowRegistrationForm={setShowRegistrationForm}
      hasBeenClicked={hasBeenClicked}
      showCartUnsoldAlert={showCartUnsoldAlert}
      showSettings={showSettings}
      fromFooter={fromFooter}
      onChangeItems={onChangeItems}
      onRemoveItems={debouncedOnRemoveItems}
      setDelivery={setDelivery}
      onPropagateOrderedAmount={onPropagateOrderedAmount}
      toggleForceEditable={toggleForceEditable}
      onSwitchToEarlierDateIfExists={onSwitchToEarlierDateIfExists}
      onSubmit={onSubmit}
      onTopClick={onTopClick}
      onRegisterSuccess={onRegisterSuccess}
      onSettingsClose={onSettingsClose}
      onSettingsChange={onSettingsChange}
      colorSecondary={colorSecondary}
      logoSmallUrl={logoSmallUrl}
      taxDetails={taxDetails}
      bioCertification={bioCertification}
      companyAddress={companyAddress}
      privacyPage={privacyPage}
      gmbhName={props.gmbhName}
      logoBigUrl={props.logoBigUrl}
      logoFavIcon={props.logoFavIcon}
      phoneNumber={props.phoneNumber}
      companyEmail={props.companyEmail}
      addInvoicePaymentMethod={props.addInvoicePaymentMethod}
      addSepaPaymentMethod={props.addSepaPaymentMethod}
      creditorIdentifier={props.creditorIdentifier}
      sepaCsvEmail={props.sepaCsvEmail}
      paymentProvider={props.paymentProvider}
      webhost={props.webhost}
      colorPrimary={props.colorPrimary}
      homePage={props.homePage}
      conditionsPage={props.conditionsPage}
      companyName={props.companyName}
      isStaff={props.isStaff}
      reRender={reRender}
      setRender={setRender}
      productType={productType}
      filteredProductType={filteredProductType}
      userLikedProducts={userLikedProducts}
      hasRules={hasRules}
      ruleCount={ruleCount}
      setRuleCount={setRuleCount}
      preferencesAndRulesLoaded={preferencesAndRulesLoaded}
      setProductType={setProductType}
      setFilteredProductType={setFilteredProductType}
      setUserLikedProducts={setUserLikedProducts}
      showPreferencesFunc={showPreferencesFunc}
      showPreferences={showPreferences}
      setShowPreferences={setShowPreferences}
      deliveryAlteredText={deliveryAlteredText}
      insufficientStockAmountText={insufficientStockAmountText}
      updated_orders={props.updated_orders}
      was_updated={props.was_updated}
      cartInit={cartInit}
      fetchPreferencesAndRules={fetchPreferencesAndRules}
      isLoadingPreferencesAndRules={isLoadingPreferencesAndRules}
      fetchCatalog={fetchCatalog}
      fetchDelivery={fetchDelivery}
      subscription={subscription}
      setSubscription={setSubscription}
      searchText={searchText}
      setSearchText={setSearchText}
      filteredCatalog={filteredCatalog}
      setFilteredCatalog={setFilteredCatalog}
      search={search}
      isCatalogEmpty={isCatalogEmpty}
      productsByCategory={productsByCategory}
      filteredProductsByCategory={filteredProductsByCategory}
      onFavouriteAddRemove={onFavouriteAddRemove}
      categories={categories}
      canCancelCart={props.canCancelCart}
    />
  );
}

export default Provider(Cart);
